import React, { useContext, useEffect, useMemo, useState } from 'react';
import { CompanyContext } from '../../../../../context/CompanyContext';
import {
  barcodeFormatToString,
  Company,
  ScanConfiguration,
  scanConfigurationTypeToString,
} from '../../../../../types/company';
import { toFilterString } from '../../../../../util/string.util';
import { CreateButton } from '../../../../components/Button';
import Table from '../../../../components/Table';
import { Searchbar } from '../../../../components/TextInput';
import BarcodeConfigurationModal from './BarcodeConfigurationModal';

interface IndexedScanConfiguration extends ScanConfiguration {
  index: number;
}

export default function BarcodeConfigurationSettingsPane() {
  const { currentCompany } = useContext(CompanyContext);

  const scanConfigurations: IndexedScanConfiguration[] = useMemo(() => {
    return (currentCompany.settings.scanConfigurations || []).map((configuration, index) => ({
      ...configuration,
      index: index,
    }));
  }, [currentCompany]);

  const [items, setItems] = useState(scanConfigurations);
  const [query, setQuery] = useState('');
  const [modalProps, setModalProps] = useState({ open: false, configuration: -1 });

  useEffect(() => {
    handleSearchQueryChange(query);
  }, [scanConfigurations]);

  const handleSearchQueryChange = (query: string) => {
    setQuery(query);
    setItems(
      scanConfigurations.filter(configuration => {
        if (toFilterString(barcodeFormatToString(configuration.barcodeFormat)).includes(query)) return true;
        if (toFilterString(scanConfigurationTypeToString(configuration.type)).includes(query)) return true;
        if (toFilterString(configuration.regex).includes(query)) return true;
        return false;
      }),
    );
  };

  return (
    <>
      {modalProps.open ? (
        <BarcodeConfigurationModal
          open={modalProps.open}
          setOpen={open => setModalProps({ ...modalProps, open })}
          configuration={scanConfigurations[modalProps.configuration]}
          onComplete={configuration => {
            if (!configuration) {
              scanConfigurations[modalProps.configuration] = {
                ...scanConfigurations[modalProps.configuration],
                index: -1,
              };
            } else if (modalProps.configuration === -1) {
              scanConfigurations.push({ ...configuration.toScanConfiguration(), index: scanConfigurations.length });
            } else {
              scanConfigurations[modalProps.configuration] = {
                ...configuration.toScanConfiguration(),
                index: modalProps.configuration,
              };
            }
            const copy = new Company(currentCompany);
            copy.settings.scanConfigurations = scanConfigurations
              .filter(config => config.index !== -1)
              .map(config => {
                delete (config as any).index;
                return config;
              });
            return copy;
          }}
        />
      ) : null}

      <div className='flex h-full flex-col gap-8'>
        <div className='grid gap-2'>
          <p className='page-title'>Create and manage barcode configurations</p>
          <p className='subtitle'>
            Use this section to configure custom or non-standard barcodes and labels used in your operations. If your
            business uses proprietary label formats or specific barcode layouts that differ from industry standards, you
            can define them here. This ensures that Ventory can accurately read, interpret, and process your unique
            barcodes during scanning and inventory activities.
          </p>
        </div>
        <div className='flex flex-grow flex-col gap-4'>
          <div className='flex items-center justify-between'>
            <Searchbar
              value={query}
              onChange={handleSearchQueryChange}
              placeholder={'Search by barcode configuration'}
            />
            <CreateButton onClick={() => setModalProps({ open: true, configuration: -1 })} />
          </div>
          <Table
            items={items}
            onClick={item => setModalProps({ open: true, configuration: item.index })}
            headers={[
              { name: 'Type', key: 'type', text: item => scanConfigurationTypeToString(item.type) },
              {
                name: 'Barcode Format',
                key: 'barcode_format',
                text: item => barcodeFormatToString(item.barcodeFormat),
              },
              { name: 'Regex', key: 'regex', text: item => item.regex },
            ]}
          />
        </div>
      </div>
    </>
  );
}
